// ============================================================
// LinuxFirewall.jsx
//
// Branded, responsive course content page for the
// "Linux Firewall & Network Security" lesson.
//
// Brand palette (matches LinuxIntro.jsx):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Alert,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import ShieldIcon              from "@mui/icons-material/Shield";
import SettingsIcon            from "@mui/icons-material/Settings";
import SaveIcon                from "@mui/icons-material/Save";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import LockIcon                from "@mui/icons-material/Lock";
import NetworkCheckIcon        from "@mui/icons-material/NetworkCheck";
import SecurityIcon            from "@mui/icons-material/Security";
import BlockIcon               from "@mui/icons-material/Block";
import CheckIcon               from "@mui/icons-material/Check";
import StorageIcon             from "@mui/icons-material/Storage";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours here so a junior dev can retheme
// the entire file by editing just this one object.
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
    danger:      "rgba(255,80,80,0.15)",
    dangerBorder:"rgba(255,80,80,0.35)",
    dangerText:  "#ff8080",
    success:     "rgba(39,201,63,0.12)",
    successBorder:"rgba(39,201,63,0.35)",
    successText: "#27c93f",
};

// ── Shared heading style ───────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Consistent gold-icon + heading used across every section.
//  Props:
//    icon  – MUI icon element (e.g. <ShieldIcon />)
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            {/* Gold icon circle */}
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>

            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card showing one firewall concept.
//  Props:
//    icon        – MUI icon element
//    title       – short concept name
//    description – 1-2 sentence plain-English explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{
                        fontFamily: "'Georgia', serif",
                        fontWeight: 700,
                        color: BRAND.white,
                        fontSize: 14,
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard button.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);

    // Join all commands into a single copyable string
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* Decorative macOS-style dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button with tick feedback */}
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey inline comment above the command */}
                        {comment && (
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    color: "rgba(255,255,255,0.3)",
                                    mb: 0.3,
                                }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all", // prevent overflow on small screens
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: RuleRow
//  Used inside the UFW quick-reference table to show a
//  single rule with its intent colour-coded (allow/deny).
//  Props:
//    cmd    – the shell command string
//    effect – "allow" | "deny" | "neutral"
//    label  – plain-English label
// ============================================================
function RuleRow({ cmd, effect, label }) {
    // Pick a colour based on whether this rule allows or blocks traffic
    const effectColor =
        effect === "allow"
            ? BRAND.successText
            : effect === "deny"
                ? BRAND.dangerText
                : BRAND.muted;

    const effectBg =
        effect === "allow"
            ? BRAND.success
            : effect === "deny"
                ? BRAND.danger
                : "transparent";

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",          // wrap on very small screens
                gap: 1.5,
                py: 1.2,
                px: 1.5,
                borderBottom: `1px solid ${BRAND.border}`,
                "&:last-child": { borderBottom: "none" },
                "&:hover": { background: "rgba(255,255,255,0.02)" },
                transition: "background 0.2s",
            }}
        >
            {/* Allow / Deny badge */}
            <Chip
                icon={
                    effect === "allow"
                        ? <CheckIcon sx={{ fontSize: "13px !important", color: `${BRAND.successText} !important` }} />
                        : effect === "deny"
                            ? <BlockIcon sx={{ fontSize: "13px !important", color: `${BRAND.dangerText} !important` }} />
                            : null
                }
                label={effect === "neutral" ? "info" : effect}
                size="small"
                sx={{
                    background: effectBg,
                    border: `1px solid ${effectColor}`,
                    color: effectColor,
                    fontFamily: "monospace",
                    fontSize: 10,
                    height: 22,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    flexShrink: 0,
                }}
            />

            {/* Command in gold monospace */}
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: 12, md: 13 },
                    color: BRAND.codeText,
                    flex: 1,
                    minWidth: 0,           // allow truncation if needed
                    wordBreak: "break-all",
                }}
            >
                {cmd}
            </Typography>

            {/* Plain-English label */}
            <Typography
                sx={{
                    fontSize: 12,
                    color: BRAND.muted,
                    flexShrink: 0,
                    display: { xs: "none", sm: "block" }, // hide on tiny screens
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for deeper/optional content sections.
//  Props:
//    title    – accordion header text
//    icon     – MUI icon element
//    children – JSX rendered inside when expanded
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* Clickable header */}
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* Collapsible body using MUI Collapse for smooth animation */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConfigFile
//  Displays a config file path with a description.
//  Props:
//    path        – file path string
//    description – what the file does
// ============================================================
function ConfigFile({ path, description }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                mb: 1.2,
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                transition: "border-color 0.25s",
                "&:hover": { borderColor: BRAND.borderHover },
            }}
        >
            <FolderOpenIcon sx={{ color: BRAND.gold, fontSize: 20, mt: 0.1, flexShrink: 0 }} />
            <Box>
                <Typography
                    sx={{
                        fontFamily: "monospace",
                        fontSize: { xs: 12, md: 13 },
                        color: BRAND.goldBright,
                        fontWeight: 600,
                        mb: 0.4,
                    }}
                >
                    {path}
                </Typography>
                <Typography sx={{ fontSize: 12, color: BRAND.muted, lineHeight: 1.6 }}>
                    {description}
                </Typography>
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row for the Resources section.
//  Props:
//    href  – full URL
//    label – display name
//    sub   – short description
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxFirewall
//  Full "Linux Firewall & Network Security" lesson page.
// ============================================================
export default function LinuxFirewall() {
    return (
        // ── Page wrapper ─────────────────────────────────────
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture for depth — matches LinuxIntro
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    {/* Module badge */}
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Security · Module 04"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Lesson title with gold gradient on key word */}
                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Firewall</span> &amp; Network Security
                    </Typography>

                    {/* Intro paragraph */}
                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 660,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        Firewalls are the first line of defence for any Linux server. They inspect every
                        network packet and decide whether to allow or block it based on rules you define.
                        Linux ships with the powerful <strong style={{ color: BRAND.white }}>iptables</strong> engine
                        at its core, and wraps it with the beginner-friendly{" "}
                        <strong style={{ color: BRAND.white }}>ufw</strong> tool on Ubuntu/Debian systems.
                    </Typography>

                    {/* Quick-stats chips */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            justifyContent: { xs: "center", md: "flex-start" },
                        }}
                    >
                        {[
                            { label: "~2.5 hours", sub: "read time" },
                            { label: "Intermediate", sub: "level" },
                            { label: "2 tools", sub: "ufw & iptables" },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <LocalFireDepartmentIcon />,
                                title: "What Is a Firewall?",
                                description:
                                    "A firewall is a security system that monitors and controls network traffic based on predefined rules. On Linux, it sits between your machine and the internet — deciding what gets in and out.",
                            },
                            {
                                icon: <NetworkCheckIcon />,
                                title: "Packet Filtering",
                                description:
                                    "Traffic travels in small units called packets. A firewall inspects each packet's source, destination, port, and protocol, then applies a matching rule: ACCEPT, DROP, or REJECT.",
                            },
                            {
                                icon: <ShieldIcon />,
                                title: "UFW (Uncomplicated Firewall)",
                                description:
                                    "UFW is a beginner-friendly frontend for iptables, default on Ubuntu. Its syntax is human-readable: 'ufw allow 80' is much easier than writing raw iptables rules.",
                            },
                            {
                                icon: <SettingsIcon />,
                                title: "iptables",
                                description:
                                    "iptables is the underlying Linux firewall engine. It uses chains (INPUT, OUTPUT, FORWARD) and tables (filter, nat, mangle) for fine-grained, production-grade control.",
                            },
                            {
                                icon: <LockIcon />,
                                title: "Ports & Protocols",
                                description:
                                    "Rules target specific ports (SSH=22, HTTP=80, HTTPS=443) and protocols (tcp/udp). Blocking unused ports shrinks your attack surface significantly.",
                            },
                            {
                                icon: <StorageIcon />,
                                title: "Persistence",
                                description:
                                    "iptables rules vanish on reboot unless saved. UFW rules survive automatically. For iptables, use iptables-save / iptables-restore or the iptables-persistent package.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – UFW
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="Using UFW (Ubuntu / Debian)" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        UFW is installed by default on Ubuntu. It is the recommended starting point for
                        juniors — its rules read almost like plain English. Always check the status before
                        making changes, and <strong style={{ color: BRAND.white }}>always allow SSH (port 22)
                        before enabling the firewall</strong> to avoid locking yourself out of a remote server.
                    </Typography>

                    {/* ── Warning banner: don't lock yourself out ── */}
                    {/*
            We use a custom Box instead of MUI Alert here so we can apply
            full brand styling. Alert doesn't support our dark theme cleanly.
          */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            p: 2,
                            mb: 3,
                            background: BRAND.danger,
                            border: `1px solid ${BRAND.dangerBorder}`,
                            borderRadius: 2,
                        }}
                    >
                        <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
                        <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                            <strong>Critical:</strong> If you are on a remote server (VPS, cloud), always run{" "}
                            <code style={{ background: "rgba(0,0,0,0.3)", padding: "1px 5px", borderRadius: 3 }}>
                                sudo ufw allow 22
                            </code>{" "}
                            <em>before</em>{" "}
                            <code style={{ background: "rgba(0,0,0,0.3)", padding: "1px 5px", borderRadius: 3 }}>
                                sudo ufw enable
                            </code>
                            . Forgetting this will lock you out permanently.
                        </Typography>
                    </Box>

                    {/* ── UFW quick-reference table ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 UFW Quick Reference
                    </Typography>

                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                            mb: 3,
                        }}
                    >
                        {/* Table header row */}
                        <Box
                            sx={{
                                display: { xs: "none", sm: "flex" },
                                gap: 1.5,
                                px: 1.5,
                                py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                            }}
                        >
                            {/* Column widths match RuleRow layout */}
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", width: 70 }}>Effect</Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", flex: 1 }}>Command</Typography>
                            <Typography sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", display: { xs: "none", sm: "block" } }}>Description</Typography>
                        </Box>

                        {/* One RuleRow per UFW command */}
                        {[
                            { cmd: "$ sudo ufw status",          effect: "neutral", label: "Show current firewall status & rules" },
                            { cmd: "$ sudo ufw status verbose",   effect: "neutral", label: "Show detailed status with default policies" },
                            { cmd: "$ sudo ufw allow 22",         effect: "allow",   label: "Allow SSH (do this FIRST)" },
                            { cmd: "$ sudo ufw allow 80/tcp",     effect: "allow",   label: "Allow HTTP web traffic" },
                            { cmd: "$ sudo ufw allow 443/tcp",    effect: "allow",   label: "Allow HTTPS web traffic" },
                            { cmd: "$ sudo ufw allow 3306",       effect: "allow",   label: "Allow MySQL database connections" },
                            { cmd: "$ sudo ufw deny 23",          effect: "deny",    label: "Block Telnet (insecure — always block this)" },
                            { cmd: "$ sudo ufw deny from 192.168.1.100", effect: "deny", label: "Block all traffic from a specific IP" },
                            { cmd: "$ sudo ufw delete allow 22",  effect: "deny",    label: "Remove a previously added rule" },
                            { cmd: "$ sudo ufw enable",           effect: "neutral", label: "Activate the firewall (survives reboots)" },
                            { cmd: "$ sudo ufw disable",          effect: "neutral", label: "Deactivate the firewall" },
                            { cmd: "$ sudo ufw reset",            effect: "neutral", label: "Reset all rules back to defaults" },
                        ].map((row) => (
                            <RuleRow key={row.cmd} {...row} />
                        ))}
                    </Box>

                    {/* ── Expanded examples: UFW app profiles ── */}
                    <ExpandableExample title="UFW application profiles (named rules)" icon={<ShieldIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            UFW ships with pre-built profiles for common applications like{" "}
                            <code style={{ color: BRAND.goldBright }}>Nginx</code>,{" "}
                            <code style={{ color: BRAND.goldBright }}>Apache</code>, and{" "}
                            <code style={{ color: BRAND.goldBright }}>OpenSSH</code>. Instead of specifying
                            port numbers, you can reference these profiles by name — much easier to read
                            and less error-prone.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo ufw app list",               comment: "See all available application profiles" },
                                { cmd: "$ sudo ufw app info 'Nginx Full'",  comment: "Inspect what ports a profile covers" },
                                { cmd: "$ sudo ufw allow 'Nginx Full'",     comment: "Allow both HTTP (80) and HTTPS (443) for Nginx" },
                                { cmd: "$ sudo ufw allow 'OpenSSH'",        comment: "Allow SSH using the named profile" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* ── Expanded example: rate limiting ── */}
                    <ExpandableExample title="Rate limiting to prevent brute-force attacks" icon={<LockIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            UFW can automatically block IPs that attempt too many connections in a short
                            window. This is especially useful on SSH to defend against brute-force login attacks.
                            The limit rule blocks an IP after 6 connection attempts within 30 seconds.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo ufw limit ssh",        comment: "Enable rate-limiting on SSH port 22" },
                                { cmd: "$ sudo ufw limit 22/tcp",     comment: "Same thing — explicit port syntax" },
                                { cmd: "$ sudo ufw status numbered",  comment: "Show rules with index numbers for easy deletion" },
                            ]}
                        />
                        {/* Success note */}
                        <Box
                            sx={{
                                display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mt: 1,
                                background: BRAND.success, border: `1px solid ${BRAND.successBorder}`, borderRadius: 2,
                            }}
                        >
                            <CheckCircleOutlineIcon sx={{ color: BRAND.successText, fontSize: 18, flexShrink: 0, mt: 0.1 }} />
                            <Typography sx={{ color: BRAND.successText, fontSize: 12, lineHeight: 1.6 }}>
                                Best practice: combine <code>ufw limit ssh</code> with SSH key authentication
                                and disabling password login in <code>/etc/ssh/sshd_config</code> for maximum security.
                            </Typography>
                        </Box>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – IPTABLES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsIcon />} label="Using iptables (Advanced)" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        iptables is the real engine behind UFW. It uses three built-in{" "}
                        <strong style={{ color: BRAND.white }}>chains</strong>:{" "}
                        <code style={{ color: BRAND.goldBright }}>INPUT</code> (inbound traffic),{" "}
                        <code style={{ color: BRAND.goldBright }}>OUTPUT</code> (outbound traffic), and{" "}
                        <code style={{ color: BRAND.goldBright }}>FORWARD</code> (traffic routed through this machine).
                        Each rule you add is evaluated top-to-bottom — the first match wins.
                    </Typography>

                    {/* ── Core iptables commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Viewing & Managing Rules
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo iptables -L",           comment: "List all rules in all chains" },
                            { cmd: "$ sudo iptables -L -v -n",     comment: "Verbose list with packet counts (no DNS lookups)" },
                            { cmd: "$ sudo iptables -L --line-numbers", comment: "Show rule numbers for easy deletion" },
                            { cmd: "$ sudo iptables -F",           comment: "Flush (delete) ALL rules — use with caution!" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Adding Rules
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo iptables -A INPUT -p tcp --dport 22 -j ACCEPT",  comment: "Allow inbound SSH" },
                            { cmd: "$ sudo iptables -A INPUT -p tcp --dport 80 -j ACCEPT",  comment: "Allow inbound HTTP" },
                            { cmd: "$ sudo iptables -A INPUT -p tcp --dport 443 -j ACCEPT", comment: "Allow inbound HTTPS" },
                            { cmd: "$ sudo iptables -A INPUT -m state --state ESTABLISHED,RELATED -j ACCEPT", comment: "Allow replies to outbound connections" },
                            { cmd: "$ sudo iptables -A INPUT -j DROP",                       comment: "Drop ALL other inbound traffic (put this last!)" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Removing Rules
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo iptables -D INPUT 1",                              comment: "Delete rule number 1 from the INPUT chain" },
                            { cmd: "$ sudo iptables -D INPUT -p tcp --dport 80 -j ACCEPT",   comment: "Delete by matching the exact rule text" },
                        ]}
                    />

                    {/* ── Deeper iptables examples ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>
                        🔽 Advanced iptables Examples (click to expand)
                    </Typography>

                    {/* Accordion: blocking IPs */}
                    <ExpandableExample title="Block & log traffic from a specific IP" icon={<BlockIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            You can target individual IP addresses or entire CIDR ranges. The{" "}
                            <code style={{ color: BRAND.goldBright }}>-I</code> flag inserts the rule at
                            position 1 (top of the chain) so it is evaluated before any ACCEPT rules.
                            Adding a LOG action first lets you see blocked packets in{" "}
                            <code style={{ color: BRAND.goldBright }}>/var/log/syslog</code>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo iptables -I INPUT -s 203.0.113.42 -j LOG --log-prefix 'BLOCKED: '", comment: "Log packets from this IP" },
                                { cmd: "$ sudo iptables -I INPUT -s 203.0.113.42 -j DROP",   comment: "Then drop them (order matters: LOG first, DROP second)" },
                                { cmd: "$ sudo iptables -I INPUT -s 203.0.113.0/24 -j DROP", comment: "Block an entire /24 subnet" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion: connection tracking */}
                    <ExpandableExample title="Stateful connection tracking (the smart way)" icon={<NetworkCheckIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            The <code style={{ color: BRAND.goldBright }}>-m state</code> or{" "}
                            <code style={{ color: BRAND.goldBright }}>-m conntrack</code> module makes iptables
                            stateful — it remembers active connections. This lets you write a minimal ruleset:
                            block everything new except what you explicitly allow, but permit replies to your
                            own outbound requests automatically.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo iptables -A INPUT -m conntrack --ctstate INVALID -j DROP",                      comment: "Drop invalid packets immediately" },
                                { cmd: "$ sudo iptables -A INPUT -m conntrack --ctstate ESTABLISHED,RELATED -j ACCEPT",        comment: "Allow established & related connections" },
                                { cmd: "$ sudo iptables -A INPUT -i lo -j ACCEPT",                                             comment: "Always allow loopback (localhost) traffic" },
                                { cmd: "$ sudo iptables -A INPUT -p tcp --dport 22 -m conntrack --ctstate NEW -j ACCEPT",      comment: "Allow new SSH connections only" },
                                { cmd: "$ sudo iptables -P INPUT DROP",                                                        comment: "Set default policy: DROP everything else" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion: NAT / port forwarding */}
                    <ExpandableExample title="NAT & port forwarding" icon={<SettingsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            iptables can also do Network Address Translation (NAT) — useful for routing traffic
                            on a gateway server or redirecting ports on your local machine.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo iptables -t nat -A PREROUTING -p tcp --dport 80 -j REDIRECT --to-port 8080", comment: "Redirect port 80 to 8080 (run app without root)" },
                                { cmd: "$ sudo iptables -t nat -L",                                                          comment: "List NAT table rules" },
                                { cmd: "$ sudo sysctl -w net.ipv4.ip_forward=1",                                            comment: "Enable IP forwarding (needed for routing/NAT)" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – SAVE & RESTORE
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SaveIcon />} label="Save & Restore iptables Rules" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        iptables rules live in memory and are <strong style={{ color: BRAND.white }}>lost on reboot</strong>.
                        You must explicitly save and restore them, or install the{" "}
                        <code style={{ color: BRAND.goldBright }}>iptables-persistent</code> package to make
                        this automatic. UFW handles this for you automatically — another reason it is
                        preferred on Ubuntu.
                    </Typography>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Manual Save & Restore
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo iptables-save > /etc/iptables.rules",    comment: "Save current rules to a file" },
                            { cmd: "$ sudo iptables-restore < /etc/iptables.rules", comment: "Load rules back from the file" },
                        ]}
                    />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Automatic Persistence (Recommended)
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo apt install iptables-persistent",    comment: "Install the persistence package" },
                            { cmd: "$ sudo netfilter-persistent save",          comment: "Save rules to /etc/iptables/rules.v4 (IPv4)" },
                            { cmd: "$ sudo netfilter-persistent reload",        comment: "Reload rules without rebooting" },
                            { cmd: "$ sudo systemctl enable netfilter-persistent", comment: "Ensure rules load automatically on boot" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – CONFIG FILES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderOpenIcon />} label="Important Config Files" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Understanding where Linux stores firewall configuration lets you audit, back up,
                        and restore rules without needing to remember every command.
                    </Typography>

                    {[
                        {
                            path: "/etc/ufw/ufw.conf",
                            description: "Main UFW configuration. Controls whether UFW is enabled on boot and the logging level (off, low, medium, high, full).",
                        },
                        {
                            path: "/etc/ufw/before.rules",
                            description: "UFW rules processed before the user-defined rules. Modify this to add low-level iptables rules that UFW doesn't expose via its CLI.",
                        },
                        {
                            path: "/etc/ufw/after.rules",
                            description: "UFW rules processed after user-defined rules. Useful for catch-all LOG or DROP rules at the end of the chain.",
                        },
                        {
                            path: "/etc/ufw/user.rules",
                            description: "Auto-generated file containing all rules you added via the ufw command. Do not edit this directly — use the ufw CLI instead.",
                        },
                        {
                            path: "/etc/iptables/rules.v4",
                            description: "Persistent IPv4 iptables rules saved by iptables-persistent / netfilter-persistent. Automatically loaded on boot.",
                        },
                        {
                            path: "/etc/iptables/rules.v6",
                            description: "Same as rules.v4 but for IPv6 (ip6tables). Important to configure if your server has IPv6 enabled.",
                        },
                    ].map((f) => (
                        <ConfigFile key={f.path} {...f} />
                    ))}
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "What a firewall is and why every server needs one",
                            "How UFW works and how to enable it safely on Ubuntu",
                            "How to write allow and deny rules for specific ports and IPs",
                            "How to use UFW application profiles for cleaner rule management",
                            "How rate-limiting protects SSH from brute-force attacks",
                            "The iptables chain model: INPUT, OUTPUT, and FORWARD",
                            "How to write stateful iptables rules using connection tracking",
                            "How to persist iptables rules across reboots",
                            "Where firewall configuration files live on disk",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://help.ubuntu.com/community/UFW"
                        label="UFW – Ubuntu Community Docs"
                        sub="Official Ubuntu guide covering all UFW commands with clear examples."
                    />
                    <ResourceLink
                        href="https://wiki.archlinux.org/title/Iptables"
                        label="Arch Wiki – iptables"
                        sub="The most comprehensive iptables reference available — essential bookmarking."
                    />
                    <ResourceLink
                        href="https://linuxize.com/post/how-to-set-up-a-firewall-with-ufw-on-ubuntu-20-04/"
                        label="UFW Setup Guide – Linuxize"
                        sub="Step-by-step tutorial for setting up UFW on Ubuntu 20.04 and later."
                    />
                    <ResourceLink
                        href="https://www.digitalocean.com/community/tutorials/iptables-essentials-common-firewall-rules-and-commands"
                        label="DigitalOcean – iptables Essentials"
                        sub="Practical, real-world iptables rules used in production environments."
                    />
                    <ResourceLink
                        href="https://nftables.org/"
                        label="nftables – The Modern Replacement"
                        sub="nftables is gradually replacing iptables in newer Linux distributions. Worth knowing about once you master iptables."
                    />
                </Box>

                {/* ── Footer tip ── */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> When learning
                        iptables, always work on a local virtual machine first — never on a live remote
                        server. It is very easy to accidentally drop your SSH connection with a wrong rule.
                        Use <code style={{ color: BRAND.goldBright }}>iptables -F</code> to flush all rules
                        and start fresh if you get stuck. UFW is safer for day-to-day server management.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
